import { __assign, __rest } from "tslib";
import React, { useState } from 'react';
import classNames from 'classnames';
import pluralize from 'pluralize';
import RatingStar from './RatingStar';
import PluralText from './PluralText';
import { clamp } from '../utilities/numberUtils';
import { mapLength } from '../utilities/arrayUtils';
import './Rating.scss';
var MAX_STAR_RATING = 5;
var UNHOVERED_INDEX = -1;
var FULL_STAR_PERCENT = 100;
var EMPTY_STAR_PERCENT = 0;
var PARTIAL_STAR_INCREMENTS = 4;
var RATING_DECIMAL_DIGIT_LENGTH = 2;
var Rating = function (_a) {
    var className = _a.className, errorMessage = _a.errorMessage, label = _a.label, maxRating = _a.maxRating, onChange = _a.onChange, totalReviewers = _a.totalReviewers, value = _a.value, props = __rest(_a, ["className", "errorMessage", "label", "maxRating", "onChange", "totalReviewers", "value"]);
    var ratingClassName = classNames('Rating', className);
    var starContainerClassName = classNames('Rating__starsContainer', {
        'Rating__starsContainer--summary': totalReviewers,
        'Rating__starsContainer--ratingChange': onChange
    });
    var _b = useState(UNHOVERED_INDEX), hoveredIndex = _b[0], setHoveredIndex = _b[1];
    var valueNum = value || 0;
    var getStarPercent = function (index) {
        var valueUpByQuarter = Math.ceil(PARTIAL_STAR_INCREMENTS * valueNum) / PARTIAL_STAR_INCREMENTS;
        var fractionValue = Number(valueUpByQuarter.toFixed(RATING_DECIMAL_DIGIT_LENGTH).split('.')[1]) || EMPTY_STAR_PERCENT;
        if (fractionValue && index === Math.trunc(valueNum)) {
            return fractionValue;
        }
        else if (valueUpByQuarter > index) {
            return FULL_STAR_PERCENT;
        }
        else {
            return EMPTY_STAR_PERCENT;
        }
    };
    var getRenderedPercent = function (starIndex) {
        if (hoveredIndex === UNHOVERED_INDEX) {
            return getStarPercent(starIndex);
        }
        else if (hoveredIndex >= starIndex) {
            return FULL_STAR_PERCENT;
        }
        else {
            return EMPTY_STAR_PERCENT;
        }
    };
    var ratingClamp = clamp(valueNum, 0, maxRating !== null && maxRating !== void 0 ? maxRating : 0);
    var formattedRating = ratingClamp.toFixed(1);
    var summaryLabel = "".concat(formattedRating, " out of ").concat(maxRating, " stars");
    var ratingSummaryElem = totalReviewers ? (React.createElement("span", { className: 'Rating__summary' },
        formattedRating,
        " based on",
        ' ',
        React.createElement(PluralText, { "case": 'lowerCase', quantity: totalReviewers, shouldIncludeQuantity: true, text: 'rating' }))) : null;
    var labelElem = label ? (React.createElement("label", { className: 'Rating__label' }, label)) : null;
    var errorMessageElem = errorMessage ? (React.createElement("div", { className: 'Rating__errorMessage', role: 'alert' }, errorMessage)) : null;
    var renderStars = function () {
        return mapLength(maxRating !== null && maxRating !== void 0 ? maxRating : 0, function (index) {
            /**
             * If the user clicks on the rating that's currently selected, we want to reset the value to null
             * to effectively reset the value as if the user had never interacted with the rating in the
             * first place.  See THRIVE-10883 for additional information.
             *
             * https://thrivetrm.atlassian.net/browse/THRIVE-10883
             */
            var handleChange = function () {
                if (value === index + 1) {
                    onChange === null || onChange === void 0 ? void 0 : onChange(null);
                }
                else {
                    onChange === null || onChange === void 0 ? void 0 : onChange(index + 1);
                }
            };
            return (React.createElement(RatingStar, { "aria-label": "".concat(index + 1, " ").concat(pluralize('star', index + 1)), fillPercent: getRenderedPercent(index), key: "star-".concat(index), onClick: handleChange, onMouseEnter: function () { return onChange && setHoveredIndex(index); }, onMouseLeave: function () { return onChange && setHoveredIndex(UNHOVERED_INDEX); } }));
        });
    };
    return (React.createElement("div", __assign({ "aria-label": summaryLabel, className: ratingClassName, "data-testid": 'Rating', title: summaryLabel }, props),
        labelElem,
        React.createElement("div", { className: 'Rating__container' },
            React.createElement("div", { className: starContainerClassName }, renderStars()),
            ratingSummaryElem),
        errorMessageElem));
};
var defaultProps = {
    maxRating: MAX_STAR_RATING
};
Rating.defaultProps = defaultProps;
export default Rating;
//# sourceMappingURL=Rating.js.map